import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import {
  ClockCircleOutlined,
  ReloadOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserDeleteOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { usoSistemaService } from '../../services/usoSistemaService';
import { UsoPessoa, formatarMinutos, rotuloAcao, rotuloRota } from '../../types/usoSistema';
import { ColaboradoresModal } from './ColaboradoresModal';
import { LinhaTempoDrawer } from './LinhaTempoDrawer';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const DIAS_SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const HORAS_MAPA = Array.from({ length: 16 }, (_, i) => i + 7); // 7h–22h
const DIAS_PARA_INATIVO = 7;

const ROLE_LABEL: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  REPRESENTANTE: 'Representante',
  ASSOCIADO: 'Associado',
  SECRETARIA: 'Secretaria',
  SUPERVISOR: 'Supervisor',
  RELACIONADOR: 'Relacionador',
  LIDER_VENDAS: 'Líder de Vendas',
  POS_VENDA: 'Pós-Venda',
};

function diasDoPeriodo(inicio: Dayjs, fim: Dayjs): string[] {
  const dias: string[] = [];
  for (let d = inicio.startOf('day'); !d.isAfter(fim, 'day'); d = d.add(1, 'day')) {
    dias.push(d.format('YYYY-MM-DD'));
  }
  return dias;
}

function criarPresets(): { label: string; value: [Dayjs, Dayjs] }[] {
  const hoje = dayjs().startOf('day');
  const segunda = hoje.subtract((hoje.day() + 6) % 7, 'day');
  return [
    { label: 'Últimos 7 dias', value: [hoje.subtract(6, 'day'), hoje] },
    { label: 'Esta semana', value: [segunda, hoje] },
    { label: 'Semana passada', value: [segunda.subtract(7, 'day'), segunda.subtract(1, 'day')] },
    { label: 'Últimos 30 dias', value: [hoje.subtract(29, 'day'), hoje] },
    { label: 'Este mês', value: [hoje.startOf('month'), hoje] },
  ];
}

interface UsoSistemaPanelProps {
  tenantId: number | null;
}

export function UsoSistemaPanel({ tenantId }: UsoSistemaPanelProps) {
  const { token } = theme.useToken();
  const presets = useMemo(criarPresets, []);
  const [periodo, setPeriodo] = useState<[Dayjs, Dayjs]>(presets[0].value);
  const [modalColaboradores, setModalColaboradores] = useState(false);
  const [pessoaSelecionada, setPessoaSelecionada] = useState<UsoPessoa | null>(null);

  const inicio = periodo[0].format('YYYY-MM-DD');
  const fim = periodo[1].format('YYYY-MM-DD');

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['relatorio-uso-sistema', inicio, fim],
    queryFn: () => usoSistemaService.buscarRelatorio(inicio, fim),
    staleTime: 60_000,
  });

  const dias = useMemo(() => diasDoPeriodo(periodo[0], periodo[1]), [periodo]);

  const resumo = useMemo(() => {
    const pessoas = data?.pessoas ?? [];
    const ativasNoPeriodo = pessoas.filter(p => p.minutos_ativos > 0 || p.total_acoes > 0).length;
    const limite = dayjs().subtract(DIAS_PARA_INATIVO, 'day');
    const inativas = pessoas.filter(
      p => p.ativa && (!p.ultimo_acesso || dayjs(p.ultimo_acesso).isBefore(limite))
    ).length;
    return {
      ativasNoPeriodo,
      totalPessoas: pessoas.filter(p => p.ativa).length,
      minutos: pessoas.reduce((s, p) => s + p.minutos_ativos, 0),
      acoes: pessoas.reduce((s, p) => s + p.total_acoes, 0),
      inativas,
    };
  }, [data]);

  const serieDiaria = useMemo(
    () =>
      (data?.serie_diaria ?? []).map(d => ({
        ...d,
        label: dayjs(d.dia).format('DD/MM'),
        horas: Math.round((d.minutos / 60) * 10) / 10,
      })),
    [data]
  );

  const mapaCalor = useMemo(() => {
    const mapa = new Map<string, number>();
    let max = 0;
    (data?.mapa_calor ?? []).forEach(c => {
      mapa.set(`${c.dow}-${c.hora}`, c.minutos);
      max = Math.max(max, c.minutos);
    });
    return { mapa, max };
  }, [data]);

  const acoesPorTipo = useMemo(
    () =>
      (data?.acoes_por_tipo ?? []).map(a => ({
        nome: rotuloAcao(a.entidade, a.operacao),
        total: a.total,
      })),
    [data]
  );

  const rotas = useMemo(
    () =>
      (data?.rotas ?? []).map(r => ({
        nome: rotuloRota(r.rota),
        horas: Math.round((r.minutos / 60) * 10) / 10,
        minutos: r.minutos,
      })),
    [data]
  );

  const semDados = !!data && resumo.minutos === 0 && resumo.acoes === 0;
  const corEixo = token.colorTextSecondary;

  const corDia = (minutos: number, acoes: number) => {
    if (minutos === 0 && acoes === 0) return token.colorFillSecondary;
    if (minutos >= 120) return token.colorSuccess;
    if (minutos >= 30) return token.colorSuccessBorder;
    return token.colorSuccessBg;
  };

  const colunas = [
    {
      title: 'Pessoa',
      key: 'nome',
      render: (_: unknown, p: UsoPessoa) => (
        <Space orientation="vertical" size={2}>
          <Text strong>{p.nome || p.contas[0]?.username}</Text>
          <Space size={4} wrap>
            {p.contas.map(c => (
              <Tag key={c.profile_id} bordered={false} style={{ marginInlineEnd: 0, fontSize: 11 }}>
                {c.username} · {ROLE_LABEL[c.role ?? ''] ?? c.role}
              </Tag>
            ))}
          </Space>
        </Space>
      ),
      sorter: (a: UsoPessoa, b: UsoPessoa) => a.nome.localeCompare(b.nome),
    },
    {
      title: 'Último acesso',
      key: 'ultimo_acesso',
      width: 150,
      render: (_: unknown, p: UsoPessoa) => {
        if (!p.ultimo_acesso) return <Tag color="default">Nunca registrado</Tag>;
        const data = dayjs(p.ultimo_acesso);
        const dias = dayjs().diff(data, 'day');
        return (
          <Tooltip title={data.format('DD/MM/YYYY HH:mm')}>
            <Tag color={dias >= DIAS_PARA_INATIVO ? 'red' : dias >= 2 ? 'orange' : 'green'}>
              {dias === 0 ? `Hoje, ${data.format('HH:mm')}` : dias === 1 ? 'Ontem' : `Há ${dias} dias`}
            </Tag>
          </Tooltip>
        );
      },
      sorter: (a: UsoPessoa, b: UsoPessoa) =>
        (a.ultimo_acesso ? dayjs(a.ultimo_acesso).valueOf() : 0) -
        (b.ultimo_acesso ? dayjs(b.ultimo_acesso).valueOf() : 0),
    },
    {
      title: 'Dias no período',
      key: 'dias',
      render: (_: unknown, p: UsoPessoa) => (
        <Space orientation="vertical" size={4}>
          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', maxWidth: 260 }}>
            {dias.map(dia => {
              const d = p.por_dia[dia] ?? { minutos: 0, acoes: 0 };
              return (
                <Tooltip
                  key={dia}
                  title={`${dayjs(dia).format('ddd DD/MM')}: ${formatarMinutos(d.minutos)} ativo · ${d.acoes} ações`}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      display: 'inline-block',
                      background: corDia(d.minutos, d.acoes),
                    }}
                  />
                </Tooltip>
              );
            })}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {p.dias_ativos} de {dias.length} dias
          </Text>
        </Space>
      ),
      sorter: (a: UsoPessoa, b: UsoPessoa) => a.dias_ativos - b.dias_ativos,
    },
    {
      title: 'Tempo ativo',
      key: 'minutos',
      width: 120,
      render: (_: unknown, p: UsoPessoa) => (p.minutos_ativos ? formatarMinutos(p.minutos_ativos) : '—'),
      sorter: (a: UsoPessoa, b: UsoPessoa) => a.minutos_ativos - b.minutos_ativos,
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 100,
      render: (_: unknown, p: UsoPessoa) => {
        if (!p.total_acoes) return '—';
        const detalhe = Object.entries(p.acoes_por_tipo)
          .sort((a, b) => b[1] - a[1])
          .map(([chave, n]) => {
            const [entidade, operacao] = chave.split(':');
            return `${rotuloAcao(entidade, operacao)}: ${n}`;
          });
        return (
          <Tooltip title={<div>{detalhe.map(l => <div key={l}>{l}</div>)}</div>}>
            <Text strong style={{ cursor: 'help' }}>{p.total_acoes}</Text>
          </Tooltip>
        );
      },
      sorter: (a: UsoPessoa, b: UsoPessoa) => a.total_acoes - b.total_acoes,
    },
  ];

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <Space wrap>
          <RangePicker
            value={periodo}
            onChange={valor => valor?.[0] && valor?.[1] && setPeriodo([valor[0], valor[1]])}
            presets={presets}
            format="DD/MM/YYYY"
            allowClear={false}
            disabledDate={d => d.isAfter(dayjs(), 'day')}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Atualizar
          </Button>
        </Space>
        <Button icon={<UsergroupAddOutlined />} onClick={() => setModalColaboradores(true)}>
          Agrupar contas por pessoa
        </Button>
      </div>

      {error && (
        <Alert type="error" showIcon message="Não foi possível carregar o relatório" description={(error as Error).message} />
      )}

      {semDados && (
        <Alert
          type="info"
          showIcon
          message="Nenhuma atividade registrada neste período"
          description="O registro de uso começa a partir da implantação desta tela. Contam como uso os minutos com a aba aberta e alguma interação (clique, digitação, rolagem) nos últimos 5 minutos, além das ações de cadastro e edição."
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card variant="borderless" style={{ boxShadow: token.boxShadowTertiary }}>
            <Statistic
              title="Pessoas que usaram"
              value={resumo.ativasNoPeriodo}
              suffix={<Text type="secondary" style={{ fontSize: 14 }}>/ {resumo.totalPessoas}</Text>}
              prefix={<TeamOutlined style={{ color: token.colorPrimary }} />}
              loading={isLoading}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card variant="borderless" style={{ boxShadow: token.boxShadowTertiary }}>
            <Statistic
              title="Tempo ativo total"
              value={formatarMinutos(resumo.minutos)}
              prefix={<ClockCircleOutlined style={{ color: token.colorInfo }} />}
              loading={isLoading}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card variant="borderless" style={{ boxShadow: token.boxShadowTertiary }}>
            <Statistic
              title="Ações registradas"
              value={resumo.acoes}
              prefix={<ThunderboltOutlined style={{ color: token.colorSuccess }} />}
              loading={isLoading}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card variant="borderless" style={{ boxShadow: token.boxShadowTertiary }}>
            <Statistic
              title={`Sem acesso há ${DIAS_PARA_INATIVO}+ dias`}
              value={resumo.inativas}
              prefix={<UserDeleteOutlined style={{ color: token.colorError }} />}
              loading={isLoading}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Uso por dia" variant="borderless" style={{ boxShadow: token.boxShadowTertiary }}>
        {isLoading ? (
          <div className="flex justify-center py-16"><Spin /></div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={serieDiaria} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} vertical={false} />
              <XAxis dataKey="label" tick={{ fill: corEixo, fontSize: 11 }} />
              <YAxis yAxisId="pessoas" allowDecimals={false} tick={{ fill: corEixo, fontSize: 11 }} />
              <YAxis yAxisId="horas" orientation="right" tick={{ fill: corEixo, fontSize: 11 }} />
              <ChartTooltip
                contentStyle={{ borderRadius: 8, fontSize: 12, background: token.colorBgElevated, borderColor: token.colorBorder }}
                formatter={(valor: number, nome: string) => (nome === 'Horas ativas' ? `${valor}h` : valor)}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="pessoas" dataKey="pessoas_ativas" name="Pessoas ativas" fill={token.colorPrimary} radius={[4, 4, 0, 0]} maxBarSize={36} />
              <Line yAxisId="horas" dataKey="horas" name="Horas ativas" stroke={token.colorInfo} strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="pessoas" dataKey="acoes" name="Ações" stroke={token.colorSuccess} strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card
        title="Quem usou"
        variant="borderless"
        style={{ boxShadow: token.boxShadowTertiary }}
        extra={<Text type="secondary" style={{ fontSize: 12 }}>Clique numa pessoa para ver a linha do tempo</Text>}
      >
        <Table
          rowKey="pessoa_key"
          columns={colunas}
          dataSource={data?.pessoas ?? []}
          loading={isLoading}
          pagination={false}
          scroll={{ x: 760 }}
          size="middle"
          onRow={p => ({ onClick: () => setPessoaSelecionada(p), style: { cursor: 'pointer' } })}
          locale={{ emptyText: <Empty description="Nenhum usuário" /> }}
        />
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card title="Horários de uso" variant="borderless" style={{ boxShadow: token.boxShadowTertiary, height: '100%' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 3, fontSize: 11, color: corEixo }}>
                <thead>
                  <tr>
                    <th />
                    {HORAS_MAPA.map(h => (
                      <th key={h} style={{ fontWeight: 400, minWidth: 22 }}>{h}h</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DIAS_SEMANA.map((nomeDia, i) => (
                    <tr key={nomeDia}>
                      <td style={{ paddingRight: 6 }}>{nomeDia}</td>
                      {HORAS_MAPA.map(h => {
                        const minutos = mapaCalor.mapa.get(`${i + 1}-${h}`) ?? 0;
                        const intensidade = mapaCalor.max ? minutos / mapaCalor.max : 0;
                        return (
                          <Tooltip key={h} title={`${nomeDia} ${h}h: ${formatarMinutos(minutos)}`}>
                            <td
                              style={{
                                height: 22,
                                borderRadius: 4,
                                background: minutos
                                  ? `color-mix(in srgb, ${token.colorPrimary} ${Math.round(15 + intensidade * 85)}%, transparent)`
                                  : token.colorFillQuaternary,
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card title="Ações por tipo" variant="borderless" style={{ boxShadow: token.boxShadowTertiary, height: '100%' }}>
            {acoesPorTipo.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhuma ação no período" />
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(160, acoesPorTipo.length * 30)}>
                <BarChart data={acoesPorTipo} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                  <XAxis type="number" allowDecimals={false} tick={{ fill: corEixo, fontSize: 11 }} />
                  <YAxis type="category" dataKey="nome" width={140} tick={{ fill: corEixo, fontSize: 11 }} />
                  <ChartTooltip contentStyle={{ borderRadius: 8, fontSize: 12, background: token.colorBgElevated, borderColor: token.colorBorder }} />
                  <Bar dataKey="total" name="Total" fill={token.colorSuccess} radius={[0, 4, 4, 0]} maxBarSize={20} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>

      <Card title="Telas mais usadas" variant="borderless" style={{ boxShadow: token.boxShadowTertiary }}>
        {rotas.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhum uso no período" />
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(160, rotas.length * 30)}>
            <BarChart data={rotas} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
              <XAxis type="number" tick={{ fill: corEixo, fontSize: 11 }} />
              <YAxis type="category" dataKey="nome" width={140} tick={{ fill: corEixo, fontSize: 11 }} />
              <ChartTooltip
                contentStyle={{ borderRadius: 8, fontSize: 12, background: token.colorBgElevated, borderColor: token.colorBorder }}
                formatter={(_: number, __: string, item: { payload?: { minutos: number } }) => formatarMinutos(item.payload?.minutos ?? 0)}
              />
              <Bar dataKey="horas" name="Tempo ativo" fill={token.colorInfo} radius={[0, 4, 4, 0]} maxBarSize={20} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <ColaboradoresModal
        open={modalColaboradores}
        tenantId={tenantId}
        pessoas={data?.pessoas ?? []}
        onClose={() => setModalColaboradores(false)}
        onSalvo={() => refetch()}
      />

      <LinhaTempoDrawer
        pessoa={pessoaSelecionada}
        dias={dias}
        onClose={() => setPessoaSelecionada(null)}
      />
    </Space>
  );
}
